import React from "react";

interface State {
  error: Error | null;
}

/**
 * Last-resort screen: without it any render error leaves visitors on a completely blank
 * page. Shows what went wrong (so a non-technical player can send a screenshot) and a
 * reload button that also clears the service-worker caches in case of a stale bundle.
 */
export default class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("App crashed:", error, info.componentStack);
  }

  private handleReload = async () => {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      // reload anyway
    }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        style={{
          fontFamily: "Arial, sans-serif",
          maxWidth: 480,
          margin: "40px auto",
          padding: "0 20px",
          textAlign: "center",
        }}
      >
        <h2>Something went wrong</h2>
        <p>
          The page could not be displayed. Please tap the button below. If it
          keeps happening, send a screenshot of this page to the league admin.
        </p>
        <button
          onClick={this.handleReload}
          style={{
            padding: "12px 24px",
            fontSize: 16,
            borderRadius: 8,
            border: "none",
            background: "#1565c0",
            color: "#fff",
          }}
        >
          Reload
        </button>
        <pre
          style={{
            marginTop: 24,
            textAlign: "left",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 12,
            color: "#666",
          }}
        >
          {`Path: ${window.location.pathname}${window.location.search}`}
          {`\nTime: ${new Date().toISOString()}`}
          {`\nError: ${this.state.error.message}`}
          {`\nBrowser: ${navigator.userAgent}`}
        </pre>
      </div>
    );
  }
}
